import { GiftIcon, HomeModernIcon, MapIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { ReactNode } from 'react';
import { OptimizedImage } from './OptimizedImage';
import { Card, SectionHeader } from './ui';

interface CardItemProps {
  icon: ReactNode;
  title: string;
  description: string;
}

const gallery = [
  { src: 'maraude-bastille.webp', alt: "Distribution le long d'un campement près de la Bastille", className: 'col-span-2 row-span-2', position: 'object-[40%_50%]' },
  { src: 'benevoles-sourire.webp', alt: 'Deux bénévoles souriants avant une sortie', className: '', position: 'object-[50%_25%]' },
  { src: 'benevole-soir.webp', alt: 'Une bénévole souriante pendant une maraude du soir', className: '', position: 'object-[50%_30%]' },
  { src: 'benevole-pont.webp', alt: 'Une bénévole sur une passerelle, un sac de distribution à la main', className: '', position: 'object-[50%_30%]' },
  { src: 'maraude-de-dos.webp', alt: "Deux bénévoles de dos, gilets Nous'Rire, sacs à la main", className: '', position: 'object-[50%_40%]' },
];

const About = () => {
  return (
    <div className="relative py-20 md:py-24">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          kicker="Nos actions"
          title="Agir sur tous les fronts de la précarité"
          lead="Dans la rue, auprès des familles ou autour d'un moment partagé : nous intervenons à Paris et en banlieue là où le besoin se fait sentir."
        />

        <motion.div
          className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4"
          variants={{
            hidden: { opacity: 0 },
            show: { opacity: 1, transition: { staggerChildren: 0.1 } }
          }}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
        >
          <CardItem
            icon={<MapIcon className="h-6 w-6" />}
            title="Maraudes"
            description="Nous allons à la rencontre des personnes sans abri avec des paniers repas (plat, eau, jus, fruits, gâteau), des boissons chaudes, des vêtements et des kits d'hygiène."
          />
          <CardItem
            icon={<HomeModernIcon className="h-6 w-6" />}
            title="Aide aux familles"
            description="Grâce à nos collectes alimentaires, nous soutenons des familles démunies avec des denrées essentielles."
          />
          <CardItem
            icon={<GiftIcon className="h-6 w-6" />}
            title="Distributions"
            description="Nous organisons des distributions de repas et de produits de première nécessité, avec le soutien de nos partenaires."
          />
          <CardItem
            icon={<UserGroupIcon className="h-6 w-6" />}
            title="Événements solidaires"
            description="Nous créons des moments conviviaux pour recréer du lien et rompre l'isolement."
          />
        </motion.div>

        {/* Story */}
        <div className="panel mt-20 overflow-hidden">
          <div className="grid md:grid-cols-2">
            <div className="p-8 md:p-12 self-center">
              <p className="eyebrow mb-3">Depuis 2025</p>
              <h3 className="heading text-3xl md:text-4xl mb-6">Notre histoire</h3>
              <p className="text-brand-pink-900/75 text-lg leading-relaxed">
                Nous'Rire est née en 2025 avec une idée simple : aller à la rencontre des
                personnes en situation de précarité, plutôt que d'attendre qu'elles viennent à nous.
                Depuis, l'association a élargi son action : maraudes, distributions, collectes
                alimentaires pour les familles et événements solidaires, à Paris et en banlieue.
              </p>
              <figure className="mt-8 border-l-4 border-brand pl-5">
                <blockquote className="text-xl font-semibold text-ink leading-snug">
                  « Un repas chaud, c'est aussi un moment pour se parler. »
                </blockquote>
                <figcaption className="mt-2 text-sm text-brand-pink-900/60">L'équipe Nous'Rire</figcaption>
              </figure>
            </div>
            <div className="aspect-[4/3] md:aspect-auto md:h-full md:min-h-[460px] bg-brand-pink-100">
              <OptimizedImage
                src=""
                imageName="maraude-nuit.webp"
                alt="Trois bénévoles en route pour une maraude du soir"
                width={1200}
                height={1800}
                className="w-full h-full object-cover object-[50%_35%]"
              />
            </div>
          </div>
        </div>

        {/* Field gallery */}
        <div className="mt-20">
          <div className="flex items-end justify-between gap-4 mb-6">
            <div>
              <p className="eyebrow mb-2">Sur le terrain</p>
              <h3 className="heading text-2xl md:text-3xl">Nos bénévoles en action</h3>
            </div>
            <a
              href="https://www.instagram.com/nousrire.asso/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex text-sm font-semibold text-accent hover:underline underline-offset-4"
            >
              Plus de photos sur Instagram →
            </a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[160px] sm:auto-rows-[200px] md:auto-rows-[220px] gap-3 md:gap-4">
            {gallery.map(photo => (
              <div key={photo.src} className={`overflow-hidden rounded-2xl bg-brand-pink-100 ${photo.className}`}>
                <OptimizedImage
                  src=""
                  imageName={photo.src}
                  alt={photo.alt}
                  className={`w-full h-full object-cover transition-transform duration-500 hover:scale-105 ${photo.position}`}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const CardItem = ({ icon, title, description }: CardItemProps) => (
  <motion.div
    variants={{
      hidden: { opacity: 0, y: 20 },
      show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } }
    }}
  >
    <Card className="p-8 h-full">
      <div className="icon-badge h-12 w-12 mb-6">
        {icon}
      </div>
      <h3 className="heading text-xl mb-3">
        {title}
      </h3>
      <p className="text-brand-pink-900/75 leading-relaxed">
        {description}
      </p>
    </Card>
  </motion.div>
);

export default About;
